import {hasEventWizardUnsavedContent} from '@Components/event-wizard/event-wizard.helper';
import type {Weekdays} from '@Components/event-wizard/week-day-selectors/week-day-selectors.types';
import type {EventType} from '@Components/event-wizard/type-dropdown/type-dropdown.types';
import type {RecurringEvent, RecurringType} from '@Components/event-wizard/recurring-event-selector/recurring-event-selector.types';
import type {EventWizardReduxState, EventWizardProgressStep, EventWizardContainerQuery, EventWizardSourceType, ContainerQueryParams, RegistrationMode} from '@Components/event-wizard/event-wizard.types';
import type {TimeZone} from 'timezones-list';
import type {PlaceDetails} from '@Components/google-map/google-map.types';
import type {EventRegistrationFields} from '@Components/events/events.types';
import type {TicketingConfig, TicketingFeeParams} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';
import type {EventWizardOrganisers} from '@Components/event-wizard/event-wizard-organisers/event-wizard-organisers.types';
import type {EventVenueType, EventWizardVenue} from '@Components/event-wizard/event-wizard-venue/event-wizard-venue.types';
import type {CoverPhoto, DisplayedCoverPhoto} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.types';
import type {EventStyles} from '@Components/event-wizard/event-style/event-style.types';
import type {ColorLegibilityState, LegibilityStatus} from '@Libraries/event-colors.library';
import {PMW_COLORS_NEUTRAL} from '@Utils/color.util';
import {useContext, useEffect, useRef} from 'react';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import type {DateTimeMobilePanel} from '@Panels/event-wizard-date-time-panel/event-wizard-date-time-panel.types';
import {useAppSelector} from '@/hooks';
import type {RootState} from '@/store';
import {containerQuery, EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT} from "@Components/event-wizard/event-wizard-container-queries-context";
import {EVENT_WIZARD_PRESENTATION_CONTEXT} from '@Components/event-wizard/event-wizard-presentation-context';
import {EventWizardPresentation} from '@Components/event-wizard/event-wizard.types';

export const useEventWizardWizardContainerQuery = (): ContainerQueryParams => {
  const containerRef = useRef<HTMLDivElement>(null);
  const params = useContainerQuery(containerRef, containerQuery);

  const containerQueryParamProps: EventWizardContainerQuery = {
    isXXsmallScreen: params.xxsmall,
    isExtraSmallScreen: params.xsmall,
    isSmallScreen: params.small,
    isMediumScreen: params.medium,
    isLargeScreen: params.large,
    isExtraLargeScreen: params.xlarge,
  };

  return {
    containerQueryParamProps,
    containerRef,
    hasContainerQueryInitialized: params.xsmall !== undefined && params.small !== undefined,
  };
};

export const useEventWizardReduxState = (): EventWizardReduxState => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard;
  });
};

export const useEventWizardTitle = (): string => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.title;
  });
};

export const useEventWizardDescription = (): string => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.description;
  });
};

export const useEventWizardIsAIDescriptionGenerating = (): boolean => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.isAIDescriptionGenerating;
  });
};

export const useEventWizardTypes = (): EventType[] => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.types.allTypes;
  });
};

export const useEventWizardSelectedType = (): EventType | null => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.types.selectedType ?? null;
  });
};

export const getEventWizardSelectedTypeHID = (root: RootState): string => {
  return root.eventWizard.types.selectedType?.hashedID ?? '';
};

export const getEventWizardAIType = (root: RootState): EventType | undefined => {
  return root.eventWizard.types.aiType;
};

export const getEventWizardIsAITypeLoading = (root: RootState): boolean => {
  return !!root.eventWizard.types.isAITypeLoading;
};

export const useEventWizardIsAITypeLoading = (): boolean => {
  return useAppSelector(({eventWizard}) => {
    return !!eventWizard.types.isAITypeLoading;
  });
};

export const useEventWizardStartDateTime = (): Date => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.startDateTime;
  });
};

export const useEventWizardEndDateTime = (): Date => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.endDateTime;
  });
};

export const useEventWizardTimeZone = (): TimeZone => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.timeZone;
  });
};

export const useEventWizardCountdown = (): boolean => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.showCountdown;
  });
};

export const useEventWizardRecurringData = (): RecurringEvent => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.recurringEvent;
  });
};

export const useEventWizardIsRecurring = (): boolean => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.recurringEvent.isRecurring;
  });
};

export const useEventWizardRecurringType = (): RecurringType => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.recurringEvent.recurringType;
  });
};

export const useEventWizardRecurringLastDate = (): Date => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.recurringEvent.recurringEndDate;
  });
};

export const useEventWizardRecurringMonthOccurence = (): string => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.recurringEvent.recurringMonthly.weekdayNum;
  });
};

export const useEventWizardRecurringMonthWeekday = (): Weekdays => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.recurringEvent.recurringMonthly.weekday;
  });
};

export const useEventWizardRecurringWeeks = (): Weekdays[] => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.recurringEvent.recurringWeeks;
  });
};

export const useEventWizardUserPastVenues = (): PlaceDetails[] => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.userPastVenues;
  });
};

export const useEventWizardVenue = (): EventWizardVenue => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.venue;
  });
};

export const useEventWizardSelectedVenue = (): EventVenueType | undefined => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.venue.selectedVenueType;
  });
};

export const useEventWizardOfflineVenue = (): PlaceDetails | undefined => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.venue.offlineVenue;
  });
};

export const useEventWizardOfflineVenueAddressDirections = (): string | undefined => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.venue.offlineVenue?.addressDirections;
  });
};

export const useEventWizardOnlineVenue = (): string | undefined => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.venue.onlineVenueURL;
  });
};

export const useEventWizardRegistrationDetails = (): EventRegistrationFields => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.registrationDetails;
  });
};

export const useEventWizardTicketingConfig = (): TicketingConfig => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.ticketingConfig;
  });
};

export const useEventWizardTicketingFees = (): TicketingFeeParams | null => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.ticketingFees;
  });
};

export const useEventWizardIsTicketingCountrySupported = (): boolean => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.isTicketingCountrySupported;
  });
};

export const useEventWizardIsPaidTicketingFeatureAvailable = (): boolean => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.isPaidTicketingFeatureAvailable;
  });
};

export const useEventWizardSelectedRegistrationMode = (): RegistrationMode => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.selectedRegistrationMode;
  });
};

export const useEventWizardIsRemoveWatermark = (): boolean => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.isRemoveWatermark;
  });
};

export const useEventWizardIsSharedInTeam = (): boolean => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.isSharedInTeam;
  });
};

export const useEventWizardIsSharedAsGoogleEvent = (): boolean => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.isSharedAsGoogleEvent;
  });
};

export const useEventWizardOrganisers = (): EventWizardOrganisers => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.organisers;
  });
};

export const useEventWizardCoverPhoto = (): CoverPhoto => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.coverPhoto;
  });
};

export const useEventWizardIsCoverPhotoPreviewLoading = (): boolean => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.coverPhoto.isPreviewLoading ?? false;
  });
};

/**
 * What the cover tile and section show: the editor's instant preview while the saved design's thumbnail is pending,
 * with no loader, and otherwise the cover itself.
 */
export const useEventWizardDisplayedCoverPhoto = (): DisplayedCoverPhoto => {
  const url = useAppSelector(({eventWizard}) => {
    return getEventWizardDisplayedCoverPhotoUrl(eventWizard);
  });
  const isLoading = useAppSelector(({eventWizard}) => {
    return getIsEventWizardDisplayedCoverPhotoLoading(eventWizard);
  });
  return {url, isLoading};
};

export const getEventWizardDisplayedCoverPhotoUrl = (eventWizard: EventWizardReduxState): string | undefined => {
  return getShownCoverPhotoInstantPreviewUrl(eventWizard) ?? eventWizard.coverPhoto.uploadedUrl;
};

export const getIsEventWizardDisplayedCoverPhotoLoading = (eventWizard: EventWizardReduxState): boolean => {
  return (eventWizard.coverPhoto.isPreviewLoading ?? false) && getShownCoverPhotoInstantPreviewUrl(eventWizard) === null;
};

/** The instant preview stands in for the cover only while the thumbnail it stands in for is still pending. */
const getShownCoverPhotoInstantPreviewUrl = (eventWizard: EventWizardReduxState): string | null => {
  return eventWizard.coverPhoto.isPreviewLoading ? eventWizard.coverPhotoInstantPreviewUrl : null;
};

export const useEventWizardShouldCheckErrState = (): boolean => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.shouldCheckIfInErrState;
  });
};

export const useEventWizardStyles = (): EventStyles => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.styles;
  });
};

export const getEventWizardStyleColor = (state: RootState): string => {
  return state.eventWizard.styles.colors.primary;
};

/**
 * The color the styles panel's content row edits: the derived page text once a custom background is
 * set (decision D7), the brand color otherwise. Not the same as the accent, which is what
 * `getEventWizardStyleColor` returns.
 */
export const getEventWizardContentColor = (state: RootState): string => {
  const {colors} = state.eventWizard.styles;
  return colors.background ? colors.dark : colors.primary;
};

/** The colours currently on the page, in the shape the legibility machine takes. */
export const getEventWizardColorLegibilityState = (state: RootState): ColorLegibilityState => {
  return {
    background: state.eventWizard.styles.colors.background ?? null,
    contentColor: getEventWizardContentColor(state),
    colorToRestoreOnUndo: state.eventWizard.styleLegibility.colorToRestoreOnUndo,
    status: state.eventWizard.styleLegibility.status,
  };
};

/** The background row's swatch; white stands for "no custom background". */
export const getEventWizardBackgroundColor = (state: RootState): string => {
  return state.eventWizard.styles.colors.background ?? PMW_COLORS_NEUTRAL.NEUTRAL_0;
};

export const getEventWizardLegibilityStatus = (state: RootState): LegibilityStatus => {
  return state.eventWizard.styleLegibility.status;
};

export const useEventWizardLegibilityStatus = (): LegibilityStatus => {
  return useAppSelector(getEventWizardLegibilityStatus);
};

export const useEventWizardColorToRestoreOnUndo = (): string | null => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.styleLegibility.colorToRestoreOnUndo;
  });
};


export const getEventWizardStyleFont = (state: RootState): string => {
  return state.eventWizard.styles.fontFamily;
};

export const useEventWizardGoogleMapAPIKey = (): string => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.googleMapAPIKey;
  });
};

export const useEventWizardTeamName = (): string | undefined => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.teamName;
  });
};

export const useEventWizardCurrentProgressStep = (): EventWizardProgressStep => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.currentProgressStep;
  });
};

export const useEventWizardIsEventBeingCreated = (): boolean => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.isEventBeingCreated;
  });
};

export const useEventWizardIsDraftBeingSaved = (): boolean => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.isDraftBeingSaved;
  });
};

export const useEventWizardIsEditingPublishedEvent = (): boolean => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.isEditingPublishedEvent;
  });
};

/**
 * Whether "Save as draft" is offered at all. A published event is never offered it: saving one as a draft
 * would un-publish it, and product settled on 2026-09-18 that un-publishing is not a thing the wizard does.
 *
 * Lives here rather than inside the action alone because both surfaces that render the action need the
 * boolean for their own layout -- the nav skips its wrapper and the small-screen menu, which holds nothing
 * else, hides entirely rather than opening empty. The backend enforces the same rule in
 * UpsertEventMaker::assertPublishedEventIsNotUnpublished_().
 */
export const useEventWizardIsSaveAsDraftOffered = (): boolean => {
  return !useEventWizardIsEditingPublishedEvent();
};

/**
 * Whether the wizard holds content that has not been saved yet.
 *
 * Derived rather than tracked: the live payload is compared against the one captured at the last save, so
 * "just saved and nothing touched since" reads as false without any reducer having to remember it.
 */
export const useEventWizardHasUnsavedChanges = (): boolean => {
  return hasEventWizardUnsavedContent(useEventWizardReduxState());
};

export const useEventWizardShouldNextBtnBeDisabled = (): boolean => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.shouldNextBtnBeDisabled;
  });
};

export const useEventWizardShouldExpandReviewSection = (): boolean => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.shouldUpdateExpandedReviewSection;
  });
};

export const useEventWizardPageSourceType = (): EventWizardSourceType => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.pageSourceType;
  });
};

export const useEventWizardDateTimeMobilePanel = (): DateTimeMobilePanel => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.dateTimeMobilePanel;
  });
};

export const useEventWizardIsDesignPrefillContentLoading = (): boolean => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.isDesignPrefillContentLoading;
  });
};

export const useEventWizardProgressSectionLandedOn = (): EventWizardProgressStep => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.progressStepLandedOn;
  });
};

export const useEventWizardDoesPAYGUserHaveAITemplateLimit = (): boolean => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.doesPAYGUserHaveAITemplateLimit;
  });
};

export const useEventWizardIsStylesPanelOpen = (): boolean => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.isStylesPanelOpen;
  });
};

export const useEventWizardShouldUpsellRecurring = (): boolean => {
  return useAppSelector(({eventWizard}) => {
    return eventWizard.shouldUpsellRecurring;
  });
};

/**
 * Asks before the browser leaves the wizard page with unsaved work. The page mounts it; an embed has no page to
 * leave. It asks the same "is there anything to save?" question the Save draft button asks, so a successful draft save
 * silences the prompt, and reads the store when the browser asks rather than when the page rendered.
 */
export const useWarnBeforeLeavingWithUnsavedEvent = (): void => {
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent): string | undefined => {
      if (canLeaveWithoutWarning()) {
        return undefined;
      }
      e.preventDefault();
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return (): void => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
};

const canLeaveWithoutWarning = (): boolean => {
  const eventWizardState = window.PMW.redux.store.getState().eventWizard;

  if (eventWizardState.isEventBeingCreated) {
    return true;
  }
  return !hasEventWizardUnsavedContent(eventWizardState);
};

/** True inside an embedding modal. Read it only where a branch already exists; layout differences are CSS variables. */
export const useIsEventWizardEmbedded = (): boolean => {
  return useContext(EVENT_WIZARD_PRESENTATION_CONTEXT) === EventWizardPresentation.EMBEDDED;
};

/**
 * Date-time and venue expand inline on wide layouts and open as their own modal on narrow ones. Inside an embedding modal
 * they always open as a modal, over the embed, instead of expanding inside it.
 */
export const useShouldPanelOpenInModal = (): boolean => {
  const {isSmallScreen} = useContext(EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT);
  const isEmbedded = useIsEventWizardEmbedded();

  return isSmallScreen === true || isEmbedded;
};
